/**
 * platform-channels-page.tsx — Sức khoẻ kênh toàn hệ thống. Route `/platform/channels`.
 * Trả lời nhanh: org nào đang rớt Zalo/OA, token OA nào sắp hết hạn.
 */
import { useState } from 'react'
import dayjs from 'dayjs'
import { PageHeader } from '@/components/shared/page-header'
import { StatCard } from '@/components/shared/stat-card'
import { DataTable, type Column } from '@/components/shared/data-table'
import { ErrorState } from '@/components/shared/feedback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PlatformTag } from '@/components/shared/platform-mark'
import { usePlatformChannelHealth, type ChannelHealthItem } from '@/hooks/use-platform'

export function PlatformChannelsPage() {
  const { data, isLoading, isError } = usePlatformChannelHealth()
  const [onlyIssues, setOnlyIssues] = useState(true)

  const all = data?.items ?? []
  const rows = onlyIssues
    ? all.filter((i) => !i.isDisabled && (i.status !== 'connected' || i.tokenExpired || i.tokenExpiring))
    : all

  const columns: Column<ChannelHealthItem>[] = [
    {
      key: 'org', header: 'Tổ chức',
      cell: (r) => <span className="font-medium">{r.orgName}</span>,
    },
    {
      key: 'channel', header: 'Kênh',
      cell: (r) => (
        <div className="min-w-0">
          <p className="truncate">{r.displayName || '(không tên)'}</p>
          <PlatformTag platform={r.platform} className="mt-0.5" />
        </div>
      ),
    },
    {
      key: 'status', header: 'Trạng thái',
      cell: (r) =>
        r.isDisabled ? <Badge variant="secondary">Đã tắt</Badge>
        : r.status === 'connected' ? <Badge variant="success">Đã kết nối</Badge>
        : <Badge variant="destructive">Mất kết nối</Badge>,
    },
    {
      key: 'token', header: 'Token OA',
      cell: (r) =>
        !r.tokenExpiresAt ? <span className="text-muted-foreground">—</span>
        : r.tokenExpired ? <Badge variant="destructive">Hết hạn</Badge>
        : r.tokenExpiring ? <Badge variant="warning">Sắp hết hạn</Badge>
        : <span className="text-xs text-muted-foreground">{dayjs(r.tokenExpiresAt).format('DD/MM/YYYY')}</span>,
    },
    {
      key: 'last', header: 'Kết nối lần cuối',
      cell: (r) => (
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {r.lastConnectedAt ? dayjs(r.lastConnectedAt).format('DD/MM/YYYY HH:mm') : 'Chưa từng'}
        </span>
      ),
    },
  ]

  const s = data?.summary

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sức khoẻ kênh"
        description={`Kênh giao tiếp của mọi tổ chức. Token OA cảnh báo trước ${data?.tokenWarnDays ?? 7} ngày.`}
      />

      {s && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Tổ chức đang có sự cố" value={s.orgsWithIssue} />
          <StatCard label="Kênh mất kết nối" value={s.disconnected} />
          <StatCard label="Token hết hạn" value={s.tokenExpired} />
          <StatCard label="Token sắp hết hạn" value={s.tokenExpiring} />
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button variant={onlyIssues ? 'default' : 'outline'} size="sm" onClick={() => setOnlyIssues(true)}>
          Chỉ kênh có vấn đề
        </Button>
        <Button variant={onlyIssues ? 'outline' : 'default'} size="sm" onClick={() => setOnlyIssues(false)}>
          Tất cả ({all.length})
        </Button>
      </div>

      {isError ? (
        <ErrorState />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          loading={isLoading}
          emptyTitle={onlyIssues ? 'Không kênh nào có vấn đề 🎉' : 'Chưa có kênh nào'}
        />
      )}
    </div>
  )
}
